import { Bell, Search, Sun, Moon, ChevronRight, LogOut, KeyRound } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { toast } from "sonner";
import type { ApiError, NotificationDto } from "@procureflow/shared-types";
import { useNotificationStream } from "@/lib/use-notification-stream";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChangePasswordDialog } from "@/components/change-password-dialog";
import type { ChangePasswordPayload } from "@/components/change-password-form";
import { UserAvatar } from "@/components/user-avatar";
import { AvatarUpload } from "@/components/avatar-upload";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth } from "@/lib/auth-client";

const titleMap: Record<string, string> = {
  "/": "Dashboard",
  "/purchase-requests": "Purchase Requests",
  "/approvals": "Approvals",
  "/rfq": "RFQ Management",
  "/purchase-orders": "Purchase Orders",
  "/grn": "Goods Receipt Notes",
  "/invoices": "Invoices",
  "/finance": "Finance & Payments",
  "/vendors": "Vendors",
  "/notifications": "Notifications",
  "/reports": "Reports & Analytics",
  "/users": "User Management",
  "/audit-logs": "Audit Logs",
};

export function AppHeader() {
  const { user, logout } = useAuth();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const title = titleMap[path] ?? "ProcureFlow";
  const [dark, setDark] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // US-076 — self-service change password from the account menu.
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [changePwSubmitting, setChangePwSubmitting] = useState(false);
  const [changePwError, setChangePwError] = useState<string | undefined>(undefined);

  async function handleChangePassword(payload: ChangePasswordPayload) {
    setChangePwSubmitting(true);
    setChangePwError(undefined);
    try {
      const res = await fetchWithAuth("/api/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
        setChangePwError(body?.message ?? "Failed to change password.");
        return;
      }
      setChangePwOpen(false);
      toast.success("Password changed.");
    } catch {
      setChangePwError("An unexpected error occurred.");
    } finally {
      setChangePwSubmitting(false);
    }
  }

  function openChangePassword() {
    setChangePwError(undefined);
    setChangePwOpen(true);
  }

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      const res = await fetchWithAuth("/api/notifications?limit=1");
      if (!res.ok) return;
      const body = (await res.json()) as { meta?: { unread_count?: number } };
      setUnreadCount(body.meta?.unread_count ?? 0);
    } catch {
      // ignore — count refresh is best-effort
    }
  }, [user]);

  useEffect(() => {
    refreshUnread();
    const handler = () => {
      refreshUnread();
    };
    if (typeof window !== "undefined") {
      window.addEventListener("notifications:changed", handler);
      return () => window.removeEventListener("notifications:changed", handler);
    }
  }, [refreshUnread, path]);

  // US-078 (ADR-022) — live SSE push. One connection for the whole app (the
  // header is always mounted). On arrival: bump the bell, toast, and broadcast
  // `notification:new` so an open /notifications list can prepend it. Connect
  // only when logged in AND a real API is reachable — MSW does not serve SSE,
  // so pure-MSW dev stays on the local `notifications:changed` path.
  const streamEnabled =
    !!user && (!import.meta.env.DEV || import.meta.env.VITE_USE_REAL_API === "1");
  const handleIncoming = useCallback((n: NotificationDto) => {
    setUnreadCount((c) => c + 1);
    toast(n.message);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("notification:new", { detail: n }));
    }
  }, []);
  useNotificationStream(handleIncoming, streamEnabled);

  return (
    <header className="sticky top-0 z-30 glass border-b border-border">
      <div className="flex items-center gap-4 px-4 lg:px-8 h-16">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground">
              ProcureFlow
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground">{title}</span>
          </div>
          <h1 className="text-lg font-semibold tracking-tight truncate">{title}</h1>
        </div>

        <div className="hidden md:flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 w-72 shadow-elegant">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            placeholder="Search PRs, vendors, POs..."
            className="bg-transparent outline-none text-sm flex-1 placeholder:text-muted-foreground"
          />
          <kbd className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">⌘K</kbd>
        </div>

        <button
          onClick={() => setDark((d) => !d)}
          className="h-9 w-9 grid place-items-center rounded-lg border border-border bg-card hover:bg-accent transition-colors"
          aria-label="Toggle theme"
        >
          {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        <Link
          to="/notifications"
          className="relative h-9 w-9 grid place-items-center rounded-lg border border-border bg-card hover:bg-accent transition-colors"
          aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span
              data-testid="notifications-bell-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground ring-2 ring-card"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Link>

        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="h-9 w-9 rounded-full border border-border overflow-hidden hover:opacity-90 transition-opacity"
                aria-label="User menu"
              >
                <UserAvatar userId={user.id} name={user.name} size={36} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="font-normal">
                <p className="text-sm font-medium leading-none">{user.name}</p>
                <p className="text-xs text-muted-foreground mt-1 truncate">{user.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => e.preventDefault()}
                className="cursor-default focus:bg-transparent"
              >
                <AvatarUpload userId={user.id} name={user.name} />
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={openChangePassword} className="cursor-pointer">
                <KeyRound className="h-4 w-4 mr-2" />
                Change password
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={logout}
                className="text-destructive focus:text-destructive cursor-pointer"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <ChangePasswordDialog
        open={changePwOpen}
        onOpenChange={setChangePwOpen}
        onSubmit={handleChangePassword}
        submitting={changePwSubmitting}
        error={changePwError}
      />
    </header>
  );
}
